/**
 * 로딩 상태 표시 컴포넌트
 *
 * 주요 기능:
 * - 비동기 작업 중 로딩 상태를 시각적으로 표시
 * - 커스터마이징 가능한 크기와 메시지 지원
 * - 반응형 디자인으로 다양한 화면 크기 대응
 * - Suspense fallback 및 데이터 로딩 시 사용
 *
 * 사용 옵션:
 * - size: 스피너와 텍스트 크기 (small | medium | large)
 * - message: 로딩 중 표시할 메시지
 * - minHeight: 컨테이너 최소 높이 설정
 *
 * 사용처:
 * - router/index.tsx의 SuspenseWrapper fallback
 * - ProtectedRoute의 인증 확인 중 표시
 * - 데이터 페칭 중 로딩 상태 표시
 */

import React from "react";
import styled from "styled-components";
import { LoadingSpinner } from "./LoadingSpinner";

interface LoadingStateProps {
  /** 로딩 중 표시할 메시지 */
  message?: string;
  /** 스피너와 텍스트 크기 */
  size?: "small" | "medium" | "large";
  /** 컨테이너 최소 높이 */
  minHeight?: string;
  /** 추가 CSS 클래스 */
  className?: string;
}

/**
 * Loading State 컴포넌트
 *
 * 로딩 스피너와 메시지를 표시하는 중앙 정렬된 컴포넌트
 *
 * @param message - 로딩 중 표시할 메시지 (기본값: 'Loading...')
 * @param size - 스피너와 텍스트 크기 (기본값: 'medium')
 * @param minHeight - 컨테이너 최소 높이 (기본값: '400px')
 * @param className - 추가 CSS 클래스
 * @returns 로딩 상태를 표시하는 컴포넌트
 */
export const LoadingState: React.FC<LoadingStateProps> = ({
  message = "Loading...",
  size = "medium",
  minHeight = "400px",
  className,
}) => {
  return (
    <Container $minHeight={minHeight} className={className}>
      <SpinnerWrapper $size={size}>
        <LoadingSpinner />
      </SpinnerWrapper>
      <Message $size={size}>{message}</Message>
    </Container>
  );
};

// Styled Components
const Container = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 20px;
  min-height: ${({ $minHeight }) => $minHeight};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 60px 16px;
  }
`;

const SpinnerWrapper = styled.div<{ $size: "small" | "medium" | "large" }>`
  margin-bottom: ${({ $size }) => {
    switch ($size) {
      case "small":
        return "12px";
      case "large":
        return "32px";
      default:
        return "24px";
    }
  }};

  /* LoadingSpinner 크기 조정 */
  > div {
    width: ${({ $size }) => {
      switch ($size) {
        case "small":
          return "40px";
        case "large":
          return "80px";
        default:
          return "60px";
      }
    }};
    height: ${({ $size }) => {
      switch ($size) {
        case "small":
          return "40px";
        case "large":
          return "80px";
        default:
          return "60px";
      }
    }};
    border-width: ${({ $size }) => {
      switch ($size) {
        case "small":
          return "2px";
        case "large":
          return "4px";
        default:
          return "3px";
      }
    }};
  }
`;

const Message = styled.p<{ $size: "small" | "medium" | "large" }>`
  font-size: ${({ $size }) => {
    switch ($size) {
      case "small":
        return "14px";
      case "large":
        return "20px";
      default:
        return "18px";
    }
  }};
  color: ${({ theme }) => theme.colors.fontGray[300]};
  font-weight: 500;
  margin: 0;
`;
